import { RestError } from '@azure/core-http';
import {
  CreateCustomerDto,
  CustomerDto,
  KnownHubDtoStatus,
  KnownProjectDtoProjectType,
  KnownUserStatus,
  OrderDto,
  SearchCustomerDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import * as _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryGetProject, useQueryGetProjectChildProjects } from '../../../../../core/queries/projects.queries';
import {
  useMutationBookOrder,
  useMutationBookOrderWithImmediateDelivery,
  useMutationCustomerSearch
} from './book-order-action.queries';
import { useQueryListHubs } from '../../../../lockers/hubs.queries';
import { useQueryListGroups } from '../../../../groups/groups.queries';
import { OrdersListActions } from '../orders-list.actions';
import { naturalSort } from '../../../../../core/util/natural-sort.util';
import { useBookingFlowType } from './use-booking-flow-type.hook';
import { BookingFlowType } from '../../../../../ui/enums/booking-flow-type.enum';
import { foldInCreatedCustomer, SelectedCustomer } from './logic/booked-customer';

export interface BookOrderActionProps {
  projectID: string;
  onAbort(): void;
  onSuccess(action: OrdersListActions): void;
}

export interface BookOrderActionState {
  projectID: string;
  loading: boolean;
  error?: RestError;
}

export interface FindCustomerStepState extends BookOrderActionState {
  current: 'FIND_CUSTOMER';
}

export interface BookOrderStepState extends BookOrderActionState {
  current: 'BOOK_ORDER';
  searchResults: CustomerDto[];
  isCreateCustomerFormActive: boolean;
  selectedCustomer: SelectedCustomer;
}

export interface DeliverNowStepState extends BookOrderActionState {
  current: 'DELIVER_NOW';
  order: OrderDto;
}

export interface BookingProgressStepState extends BookOrderActionState {
  current: 'BOOKING_IN_PROGRESS';
  bookingData: { bookOrderForm: BookOrderForm; createCustomerForm: CreateCustomerForm };
}

export interface BookingCompletedStepState extends BookOrderActionState {
  current: 'BOOKING_COMPLETED';
  order: OrderDto;
  lastBookingData: { bookOrderForm: BookOrderForm; createCustomerForm: CreateCustomerForm };
  lastSelectedCustomer: BookOrderStepState['selectedCustomer'];
  lastSearchResults: CustomerDto[];
}

export type BookOrderActionStates =
  | FindCustomerStepState
  | BookOrderStepState
  | DeliverNowStepState
  | BookingProgressStepState
  | BookingCompletedStepState;

export type CreateCustomerForm = Partial<
  Pick<CreateCustomerDto, 'email' | 'mobile' | 'firstName' | 'lastName' | 'prefersLowerLockers' | 'groupName'>
>;

export interface BookOrderForm {
  orderNumber?: string;
  hubID?: string;
  lockerID?: string;
}

export interface BookOrderActionHandlers {
  onCancelIntent(): void;
  onSelectProject(projectID: string): void;
  onSubmitCustomerSearch(form: SearchCustomerDto): void;
  onBackToSearch(): void;
  onSelectCustomer(selection: BookOrderStepState['selectedCustomer']): void;
  onCreateNewCustomer(): void;
  onCancelCreateCustomer(): void;
  onDeliverLater(form: { bookOrderForm: BookOrderForm; createCustomerForm: CreateCustomerForm }): void;
  onDeliverNow(form: { bookOrderForm: BookOrderForm; createCustomerForm: CreateCustomerForm }): void;
  onCancelDeliverNow(): void;
  onCompleteDeliverNow(): void;
  onBookAnother(): void;
  onBookingComplete(): void;
}

function getInitialState(projectID: string): BookOrderActionStates {
  return {
    current: 'FIND_CUSTOMER',
    loading: false,
    projectID: projectID === '0824d5bd-9c70-41c8-9d87-f8f17f68cbdd' ? '' : projectID
  };
}

export function useBookOrderAction({ projectID: initialProjectId, onAbort, onSuccess }: BookOrderActionProps) {
  const [state, setState] = useState(getInitialState(initialProjectId));

  const currentProject = useQueryGetProject(initialProjectId);
  const projectType = currentProject.data?.projectType;
  // Load the selected project so we can wait for it before deciding which booking flow
  // version of the modal to show, avoiding a flash of the wrong flow (GFC-7591).
  const selectedProject = useQueryGetProject(state.projectID);
  const bookingFlowType = useBookingFlowType(state.projectID);

  const retailers = useQueryGetProjectChildProjects(initialProjectId);

  const retailerOptions = useMemo(() => {
    if (!retailers.data) return [];
    return (
      retailers.data
        .filter((item) => item.id !== '0824d5bd-9c70-41c8-9d87-f8f17f68cbdd')
        .map(({ id, name, imageURL }) => ({ label: name, imageURL, value: id })) ?? []
    );
  }, [retailers.data]);

  const selectedRetailer = useMemo(() => {
    return retailerOptions?.find(({ value }) => value === state.projectID);
  }, [state.projectID, retailerOptions]);

  const { data: hubsData, isLoading: isHubsListLoading, refetch: refetchHubs } = useQueryListHubs(state.projectID);

  // When the hubs list or the selected project is loading, the modal will be in a loading state.
  useEffect(() => {
    setState((prevState) => ({ ...prevState, loading: isHubsListLoading || selectedProject.isLoading }));
  }, [isHubsListLoading, selectedProject.isLoading]);

  // When the projectID changes, get the new list of hubs
  const hubOptions = useMemo(() => {
    const options =
      hubsData
        ?.filter((h) => h.status === KnownHubDtoStatus.Active)
        .map(({ id, name }) => ({ label: name, value: id })) ?? [];
    const sorted = naturalSort(options).asc((option) => option.label);

    // For inventory flow, don't add the "Any" option - specific hub selection is required
    if (bookingFlowType === BookingFlowType.INVENTORY) {
      return sorted;
    }

    // For traditional flow, add 'Any' option if there are multiple hubs
    if (sorted.length > 1) {
      const anyLabel = selectedRetailer?.label ? `Any at ${selectedRetailer.label}` : 'Any';
      sorted.unshift({ label: anyLabel, value: 'any' });
    }
    return sorted;
  }, [hubsData, selectedRetailer?.label, bookingFlowType]);

  // Select the first hub automatically for inventory flow, or if there's only one option
  useEffect(() => {
    if (!hubOptions || hubOptions.length === 0) return;

    // For inventory flow, always pre-select the first hub
    if (bookingFlowType === BookingFlowType.INVENTORY) {
      const currentValue = bookOrderStepForm.getValues('bookOrderForm.hubID');
      if (!currentValue || currentValue === 'any') {
        bookOrderStepForm.setValue('bookOrderForm.hubID', hubOptions[0].value);
      }
    } else if (hubOptions.length === 1) {
      // For traditional flow, only auto-select if there's exactly one option
      bookOrderStepForm.setValue('bookOrderForm.hubID', hubOptions[0].value);
    }
  }, [hubOptions, bookingFlowType]);

  // Exclude top level groups if the project type is not Retail and not Store.
  const excludeTopLevelGroups =
    projectType !== KnownProjectDtoProjectType.Retailer && projectType !== KnownProjectDtoProjectType.Store;
  const groups = useQueryListGroups(initialProjectId, excludeTopLevelGroups);

  // Group options is a list of all group names in the project (without IDs) string[]
  // This is so the user can both select an existing group as well as create a new one
  const groupOptions = useMemo(() => {
    if (!groups.data) return [];
    return groups.data.map(({ name }) => name) ?? [];
  }, [groups.data]);

  const findCustomerMutation = useMutationCustomerSearch();
  const bookOrderMutation = useMutationBookOrder();
  const bookOrderWithImmediateDeliveryMutation = useMutationBookOrderWithImmediateDelivery();

  function transistionToFindCustomerState(): void {
    findCustomerStepForm.reset(undefined, { keepIsValid: true, keepValues: true });
    setState((state) => ({
      current: 'FIND_CUSTOMER',
      loading: false,
      projectID: state.projectID
    }));
    bookOrderStepForm.reset();
  }

  function updateFindCustomerState(update: Partial<Omit<FindCustomerStepState, 'current'>>): void {
    setState((state) => {
      switch (state.current) {
        case 'FIND_CUSTOMER':
          return {
            ...state,
            ...update
          };
        default:
          return state;
      }
    });
  }

  function transistionToBookOrderState(newState: Omit<BookOrderStepState, 'current' | 'projectID'>): void {
    setState((state) => ({
      current: 'BOOK_ORDER',
      projectID: state.projectID,
      ...newState
    }));
  }

  function transistionToDeliverNowState(order: OrderDto): void {
    setState((state) => ({
      current: 'DELIVER_NOW',
      projectID: state.projectID,
      loading: false,
      order
    }));
  }

  function transistionToBookingProgressState(bookingData: {
    bookOrderForm: BookOrderForm;
    createCustomerForm: CreateCustomerForm;
  }): void {
    setState((state) => ({
      current: 'BOOKING_IN_PROGRESS',
      projectID: state.projectID,
      loading: true,
      bookingData
    }));
  }

  function transistionToBookingCompletedState(
    order: OrderDto,
    lastBookingData: { bookOrderForm: BookOrderForm; createCustomerForm: CreateCustomerForm },
    selectedCustomer: BookOrderStepState['selectedCustomer'],
    searchResults: CustomerDto[]
  ): void {
    setState((state) => ({
      current: 'BOOKING_COMPLETED',
      projectID: state.projectID,
      loading: false,
      order,
      lastBookingData,
      lastSelectedCustomer: selectedCustomer,
      lastSearchResults: searchResults
    }));
  }

  function updateBookingProgressState(update: Partial<BookingProgressStepState>): void {
    setState((state) => {
      switch (state.current) {
        case 'BOOKING_IN_PROGRESS':
          return {
            ...state,
            ...update
          };
        default:
          return state;
      }
    });
  }

  function updateBookOrderState(update: Partial<BookOrderStepState>): void {
    setState((state) => {
      switch (state.current) {
        case 'BOOK_ORDER':
          return {
            ...state,
            ...update
          };
        default:
          return state;
      }
    });
  }

  function submitOrderBookingWithProgress(form: {
    bookOrderForm: BookOrderForm;
    createCustomerForm: CreateCustomerForm;
  }) {
    if (state.current !== 'BOOK_ORDER') {
      return;
    }

    // Transition to progress step
    transistionToBookingProgressState(form);

    const customerData = state.selectedCustomer.isExisting
      ? undefined
      : cleanUpCreateCustomerForm(form.createCustomerForm);

    const customerID = state.selectedCustomer.isExisting ? state.selectedCustomer.customer.id : undefined;

    // Validate customer data if creating new customer
    if (!state.selectedCustomer.isExisting && customerData && !createCustomerFormIsComplete(customerData)) {
      updateBookingProgressState({
        loading: false,
        error: new RestError('Please complete all required customer fields')
      });
      return;
    }

    // Use appropriate mutation based on booking flow type
    if (bookingFlowType === BookingFlowType.INVENTORY) {
      // Validate locker selection for inventory flow
      if (!form.bookOrderForm.lockerID) {
        updateBookingProgressState({
          loading: false,
          error: new RestError('Please select a locker')
        });
        return;
      }

      bookOrderWithImmediateDeliveryMutation.mutate(
        {
          projectID: state.projectID,
          customerData: customerData as CreateCustomerDto | undefined,
          customerID,
          orderData: form.bookOrderForm,
          lockerID: form.bookOrderForm.lockerID
        },
        {
          onSuccess(result) {
            const { selectedCustomer, searchResults } = foldInCreatedCustomer(
              state.selectedCustomer,
              state.searchResults,
              result.createdCustomer
            );
            transistionToBookingCompletedState(result.order, form, selectedCustomer, searchResults);
          },
          onError(error) {
            let restError: RestError;
            if (error instanceof RestError) {
              restError = error;
            } else {
              restError = new RestError('There was a problem booking the order');
            }
            updateBookingProgressState({ loading: false, error: restError });
          }
        }
      );
    } else {
      // Traditional flow
      bookOrderMutation.mutate(
        {
          projectID: state.projectID,
          customerData: customerData as CreateCustomerDto | undefined,
          customerID,
          orderData: form.bookOrderForm
        },
        {
          onSuccess(result) {
            const { selectedCustomer, searchResults } = foldInCreatedCustomer(
              state.selectedCustomer,
              state.searchResults,
              result.createdCustomer
            );
            transistionToBookingCompletedState(result.order, form, selectedCustomer, searchResults);
          },
          onError(error) {
            let restError: RestError;
            if (error instanceof RestError) {
              restError = error;
            } else {
              restError = new RestError('There was a problem booking the order');
            }
            updateBookingProgressState({ loading: false, error: restError });
          }
        }
      );
    }
  }

  function submitOrderBooking(
    form: { bookOrderForm: BookOrderForm; createCustomerForm: CreateCustomerForm },
    onSuccessCb: (order: OrderDto) => void
  ) {
    if (state.current !== 'BOOK_ORDER') {
      return;
    }

    updateBookOrderState({ loading: true });

    const customerData = state.selectedCustomer.isExisting
      ? undefined
      : cleanUpCreateCustomerForm(form.createCustomerForm);

    const customerID = state.selectedCustomer.isExisting ? state.selectedCustomer.customer.id : undefined;

    // Validate customer data if creating new customer
    if (!state.selectedCustomer.isExisting && customerData && !createCustomerFormIsComplete(customerData)) {
      updateBookOrderState({
        loading: false,
        error: new RestError('Please complete all required customer fields')
      });
      return;
    }

    // Use appropriate mutation based on booking flow type
    if (bookingFlowType === BookingFlowType.INVENTORY) {
      // Validate locker selection for inventory flow
      if (!form.bookOrderForm.lockerID) {
        updateBookOrderState({
          loading: false,
          error: new RestError('Please select a locker')
        });
        return;
      }

      bookOrderWithImmediateDeliveryMutation.mutate(
        {
          projectID: state.projectID,
          customerData: customerData as CreateCustomerDto | undefined,
          customerID,
          orderData: form.bookOrderForm,
          lockerID: form.bookOrderForm.lockerID
        },
        {
          onSuccess(result) {
            onSuccessCb(result.order);
          },
          onError(error) {
            let restError: RestError;
            if (error instanceof RestError) {
              restError = error;
            } else {
              restError = new RestError('There was a problem booking the order');
            }
            updateBookOrderState({ loading: false, error: restError });
          }
        }
      );
    } else {
      // Traditional flow
      bookOrderMutation.mutate(
        {
          projectID: state.projectID,
          customerData: customerData as CreateCustomerDto | undefined,
          customerID,
          orderData: form.bookOrderForm
        },
        {
          onSuccess(result) {
            onSuccessCb(result.order);
          },
          onError(error) {
            let restError: RestError;
            if (error instanceof RestError) {
              restError = error;
            } else {
              restError = new RestError('There was a problem booking the order');
            }
            updateBookOrderState({ loading: false, error: restError });
          }
        }
      );
    }
  }

  const handlers: BookOrderActionHandlers = {
    onCancelIntent() {
      switch (state.current) {
        case 'BOOK_ORDER':
          if (state.loading) {
            return;
          } else {
            onAbort();
            break;
          }
        case 'BOOKING_IN_PROGRESS':
          if (state.error) {
            onAbort();
          }
          return;
        default:
          onAbort();
          return;
      }
    },
    onSelectProject(projectID) {
      updateFindCustomerState({ projectID });
      refetchHubs();
    },
    onSubmitCustomerSearch(form) {
      switch (state.current) {
        case 'FIND_CUSTOMER':
          updateFindCustomerState({ loading: true });
          // Ignore empty string search terms. Named rather than mapped, since `status` is set
          // below and the DTO now types it as an array.
          const searchDto = { phone: form.phone || undefined, email: form.email || undefined };
          findCustomerMutation.mutate(
            { ...searchDto, projectID: state.projectID, status: [KnownUserStatus.Active] },
            {
              onSuccess(result) {
                if (typeof result === 'undefined' || result.length === 0) {
                  transistionToBookOrderState({
                    loading: false,
                    searchResults: [],
                    isCreateCustomerFormActive: true,
                    selectedCustomer: {
                      isExisting: false
                    }
                  });
                  bookOrderStepForm.setValue('createCustomerForm.email', findCustomerForm.email);
                  bookOrderStepForm.setValue('createCustomerForm.mobile', findCustomerForm.phone);
                } else {
                  transistionToBookOrderState({
                    loading: false,
                    searchResults: result,
                    isCreateCustomerFormActive: false,
                    selectedCustomer: {
                      isExisting: true,
                      customer: result[0]
                    }
                  });
                }
              },
              onError(error) {
                let restError: RestError;
                if (error instanceof RestError) {
                  restError = error;
                } else {
                  restError = new RestError('Sorry, there was an unexpected error');
                }
                if (restError.statusCode === 404) {
                  transistionToBookOrderState({
                    loading: false,
                    searchResults: [],
                    isCreateCustomerFormActive: true,
                    selectedCustomer: {
                      isExisting: false
                    }
                  });
                } else {
                  updateFindCustomerState({
                    loading: false,
                    error: restError
                  });
                }
              }
            }
          );
      }
    },
    onBackToSearch() {
      transistionToFindCustomerState();
    },
    onSelectCustomer(selection) {
      // If the new customer form is the previous selection, we need to keep the form values but reset the rest of the form.
      // This makes the form act like it is untouched if the user interacts with it again, meaning errors caused by empty
      // required fields won't all come back at once
      if (state.current === 'BOOK_ORDER' && state.selectedCustomer.isExisting === false && selection.isExisting) {
        bookOrderStepForm.reset(undefined, { keepValues: true });
      }
      updateBookOrderState({ selectedCustomer: selection });
    },
    onCreateNewCustomer() {
      updateBookOrderState({
        isCreateCustomerFormActive: true,
        selectedCustomer: {
          isExisting: false
        }
      });
    },
    onCancelCreateCustomer() {
      if (state.current !== 'BOOK_ORDER') {
        return;
      }
      // If there are no customer search results, closing the form should not be possible
      if (state.searchResults.length === 0) {
        return;
      }
      // If the form is not currently the selected option, then just close it
      if (state.selectedCustomer.isExisting) {
        updateBookOrderState({
          isCreateCustomerFormActive: false
        });
        // Otherwise, close the form and select the first customer in the search results list
      } else {
        updateBookOrderState({
          isCreateCustomerFormActive: false,
          selectedCustomer: {
            isExisting: true,
            customer: state.searchResults[0]
          }
        });
      }
      bookOrderStepForm.reset({
        createCustomerForm: {
          email: findCustomerForm?.email,
          mobile: findCustomerForm?.phone,
          firstName: '',
          lastName: '',
          prefersLowerLockers: false,
          groupName: ''
        },
        bookOrderForm: {
          orderNumber: bookOrderForm.bookOrderForm.orderNumber
        }
      });
    },
    onDeliverLater(form) {
      if (bookingFlowType === BookingFlowType.INVENTORY) {
        // For INVENTORY: "Book Order" button uses progress flow
        submitOrderBookingWithProgress(form);
      } else {
        // For RETAIL: "Deliver Later" button uses original flow
        submitOrderBooking(form, () => onSuccess(OrdersListActions.BOOK_ORDER_SUCESS));
      }
    },
    onDeliverNow(form) {
      if (bookingFlowType === BookingFlowType.RETAIL) {
        submitOrderBooking(form, (order) => {
          transistionToDeliverNowState(order);
        });
      } else {
        submitOrderBookingWithProgress(form);
      }
    },
    onCancelDeliverNow() {
      if (state.current !== 'DELIVER_NOW') {
        return;
      }
      onSuccess(OrdersListActions.BOOK_ORDER_SUCESS);
    },
    onCompleteDeliverNow() {
      if (state.current !== 'DELIVER_NOW') {
        return;
      }
      onSuccess(OrdersListActions.BOOK_AND_DELIVER_ORDER_SUCCESS);
    },
    onBookAnother() {
      if (state.current !== 'BOOKING_COMPLETED') {
        return;
      }

      const { lastBookingData, lastSelectedCustomer, lastSearchResults } = state;

      // Reset to book order step with appropriate data based on booking flow type
      if (bookingFlowType === BookingFlowType.INVENTORY) {
        // For inventory flow: keep customer, hub, and order number
        transistionToBookOrderState({
          loading: false,
          searchResults: lastSearchResults,
          isCreateCustomerFormActive: !lastSelectedCustomer.isExisting,
          selectedCustomer: lastSelectedCustomer
        });

        // Clear the create-customer fields; resubmitting them would duplicate the customer.
        bookOrderStepForm.reset({
          createCustomerForm: lastSelectedCustomer.isExisting
            ? { email: '', mobile: '', firstName: '', lastName: '', prefersLowerLockers: false, groupName: '' }
            : lastBookingData.createCustomerForm,
          bookOrderForm: {
            orderNumber: lastBookingData.bookOrderForm.orderNumber,
            hubID: lastBookingData.bookOrderForm.hubID,
            lockerID: undefined // Clear locker selection for new booking
          }
        });
      } else {
        // For retail flow: clear form completely (start fresh)
        transistionToFindCustomerState();
      }
    },
    onBookingComplete() {
      onSuccess(OrdersListActions.BOOK_ORDER_SUCESS);
    }
  };

  const findCustomerStepForm = useForm<SearchCustomerDto>({
    mode: 'all'
  });

  const findCustomerForm = findCustomerStepForm.watch();

  const bookOrderStepForm = useForm<{
    bookOrderForm: BookOrderForm;
    createCustomerForm: CreateCustomerForm;
  }>({
    mode: 'onTouched',
    // Set default values for all fields. This ensures the form will be reset correctly
    // when the onBackToSearch handler is called
    // This is just a subset of available properties for a new customer, which are relevant to this shortened form
    defaultValues: {
      createCustomerForm: {
        email: findCustomerForm?.email,
        mobile: findCustomerForm?.phone,
        firstName: '',
        lastName: '',
        prefersLowerLockers: false
      },
      bookOrderForm: {
        orderNumber: '',
        hubID: hubOptions.length === 1 ? hubOptions[0].value : 'any'
      }
    }
  });

  const bookOrderForm = bookOrderStepForm.watch();

  return {
    state,
    handlers,
    retailerOptions,
    hubOptions,
    groupOptions,
    findCustomerStepForm,
    bookOrderStepForm,
    bookingFlowType
  } as const;
}

function createCustomerFormIsComplete(form: CreateCustomerForm): form is CreateCustomerDto {
  return !_.isNil(form.firstName);
}

function cleanUpCreateCustomerForm(form: CreateCustomerForm): CreateCustomerForm {
  return Object.entries(form).reduce((acc, [key, value]) => {
    const typedKey = key as keyof CreateCustomerForm;
    // Value should not be undefined or null or an empty string
    if (value) {
      acc[typedKey] = value as any;
    }
    return acc;
  }, {} as CreateCustomerForm);
}
